import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { fetchAdminCertificationLinkedStyles } from '@/api/(admin)/certifications';
import {
  bulkArchiveCertifications,
  cancelCertificateAiWorkflow,
  createCertification,
  createQuestionnaireRequestCertificate,
  downloadCertifications,
  exportCertifications,
  fetchCertificationLinkedStyles,
  fetchCertificationStandards,
  fetchCertifications,
  fetchExistingCertificates,
  fetchSkusList,
  fetchStylesList,
  fetchVariantsList,
  getCertificateAiWorkflow,
  triggerCertificateAiWorkflow,
  validateCertificationStandard,
} from '@/api/certifications';
import type {
  CertificationLinkedStyle,
  CertificationLinkedStylesFilters,
  CertificationLinkedStylesSort,
} from '@/api/certifications/types';
import type {
  CertificationStylesParams,
  CertificationsParams,
  CreateCertificationPayload,
} from '@/api/network/types';

export const certificationListKeys = {
  list: (companyId: string, params: Omit<CertificationsParams, 'companyId'>) =>
    [
      'network',
      'company',
      companyId,
      'certifications',
      'list',
      params,
    ] as const,
  /** Matches every certifications list, whatever the companyId and params. The
   *  companyId sits mid-key, so a prefix match can't reach these — use as an
   *  `invalidateQueries({ predicate })` after a certificate write. */
  matchesAnyList: (queryKey: readonly unknown[]) =>
    queryKey[0] === 'network' &&
    queryKey[1] === 'company' &&
    queryKey[3] === 'certifications' &&
    queryKey[4] === 'list',
};

export function useCertificationsQuery(
  companyId: string,
  params: Omit<CertificationsParams, 'companyId'>,
) {
  return useQuery({
    queryKey: certificationListKeys.list(companyId, params),
    queryFn: () => {
      const qs = new URLSearchParams({
        isCertification: 'true',
        page: String(params.page - 1),
        count: String(params.count),
      });
      // Scope by company only when an explicit companyId is provided.
      // The all-certifications page passes an empty string to opt out.
      if (companyId) qs.set('companyId', companyId);
      if (params.sort) qs.set('sort', params.sort);
      qs.set('filter', params.filter ?? '{}');
      return fetchCertifications(qs.toString());
    },
    staleTime: 60_000,
  });
}

export function useBulkArchiveCertificationsShared() {
  return useMutation({
    mutationFn: (ids: string[]) => bulkArchiveCertifications(ids),
  });
}

export function useDownloadCertificationsShared() {
  return useMutation({
    mutationFn: (ids: string[]) => downloadCertifications(ids),
  });
}

export function useExportCertificationsShared() {
  return useMutation({
    mutationFn: (ids: string[]) => exportCertifications(ids),
  });
}

export const certificationKeys = {
  list: (companyId: string, params: Omit<CertificationsParams, 'companyId'>) =>
    [
      'network',
      'company',
      companyId,
      'certifications',
      'list',
      params,
    ] as const,
  standards: () => ['certifications', 'standards'] as const,
  aiWorkflow: (workflowId: string | null) =>
    ['certifications', 'ai-workflow', workflowId] as const,
  styles: (params: Omit<CertificationStylesParams, 'page'>) =>
    ['certifications', 'styles', params] as const,
  existing: (standardId: string, companyId: string) =>
    ['certifications', 'existing', standardId, companyId] as const,
  /** Prefix for every page of a certificate's linked products — invalidate
   *  this after the links are saved. */
  linkedStyles: (certId: string) =>
    ['certifications', 'linked-styles', certId] as const,
};

export function useCertificationStandards() {
  return useQuery({
    queryKey: certificationKeys.standards(),
    queryFn: async () => {
      const res = await fetchCertificationStandards();
      return (res?.data ?? []).filter(
        s => s.type !== 'COMPANY_DOCUMENT' && s.type !== 'AUDIT',
      );
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useCertificateAiWorkflow(workflowId: string | null) {
  return useQuery({
    queryKey: certificationKeys.aiWorkflow(workflowId),
    queryFn: async () => {
      const res = await getCertificateAiWorkflow(workflowId!);
      return res.data;
    },
    enabled: !!workflowId,
    refetchInterval: query =>
      query.state.data?.status === 'PROCESSING' ? 3000 : false,
  });
}

export function useTriggerCertificateAiWorkflow() {
  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await triggerCertificateAiWorkflow(fileId);
      return res.data;
    },
  });
}

export function useCancelCertificateAiWorkflow() {
  return useMutation({
    mutationFn: (workflowId: string) => cancelCertificateAiWorkflow(workflowId),
  });
}

export function useCreateCertification() {
  return useMutation({
    mutationFn: async (payload: CreateCertificationPayload) => {
      const res = await createCertification(payload);
      return res.data;
    },
  });
}

/**
 * Creates a certificate through a questionnaire answer request rather than by
 * naming a company — the only path that works when answering on another
 * company's behalf. See `createQuestionnaireRequestCertificate`.
 */
export function useCreateQuestionnaireRequestCertificate() {
  return useMutation({
    mutationFn: async ({
      requestId,
      ...payload
    }: {
      requestId: string;
      standardId: string;
      issueDate: string;
      expirationDate: string;
      fileId?: string | null;
      issuedBy?: string | null;
      documentNumber?: string | null;
      licenseNumber?: string | null;
      explanation?: string | null;
    }) => {
      const res = await createQuestionnaireRequestCertificate(
        requestId,
        payload,
      );
      return res.data;
    },
  });
}

export function useValidateCertificationStandard() {
  return useMutation({
    mutationFn: async ({
      standardId,
      companyId,
    }: {
      standardId: string;
      companyId: string;
    }) => {
      const res = await validateCertificationStandard(standardId, companyId);
      const result = res?.[0]?.validationResult;
      const failMessages = (result?.description ?? [])
        .flat()
        .filter(d => d.type !== 'success')
        .map(d => d.text)
        .join(' ');
      return {
        valid: result?.valid ?? true,
        description: failMessages || null,
      };
    },
  });
}

const LEVEL_INCLUDE: Record<string, string[]> = {
  STYLE: ['type'],
  VARIANT: ['type', 'style'],
  SKU: ['type', 'style', 'variant'],
};

const LEVEL_FETCH = {
  STYLE: fetchStylesList,
  VARIANT: fetchVariantsList,
  SKU: fetchSkusList,
};

function buildStylesBody(
  params: Omit<CertificationStylesParams, 'page'>,
  page: number,
) {
  const level = params.level ?? 'STYLE';
  const supplierCompanyId =
    params.supplierFilter !== undefined
      ? params.supplierFilter
      : !params.myCompanyId || params.myCompanyId === params.companyId
        ? undefined
        : params.companyId;

  return {
    page,
    limit: params.limit,
    sort: 'name',
    order: 'asc',
    companyId: params.companyId,
    ...(params.certValidationStandardId
      ? { certValidationStandardId: params.certValidationStandardId }
      : {}),
    // Omit when unset — the endpoint declares these `z.string().optional()`, so
    // an explicit `null` is a 400, not "no filter" (DELTA-2453). The renew flow
    // opens the picker before either date exists.
    ...(params.certValidationIssueDate
      ? { certValidationIssueDate: params.certValidationIssueDate }
      : {}),
    ...(params.certValidationExpirationDate
      ? { certValidationExpirationDate: params.certValidationExpirationDate }
      : {}),
    ...(params.certValidationExcludeClaimId
      ? { certValidationExcludeClaimId: params.certValidationExcludeClaimId }
      : {}),
    isArchived: params.isArchived,
    ...(level !== 'STYLE' ? { isComponent: false } : {}),
    include: LEVEL_INCLUDE[level],
    ...(supplierCompanyId ? { suppliers: [supplierCompanyId] } : {}),
    ...(params.filters?.name ? { name: params.filters.name } : {}),
    ...(params.filters?.codeId || params.filters?.codeValue
      ? {
          codes: {
            ...(params.filters.codeId ? { codeId: params.filters.codeId } : {}),
            ...(params.filters.codeValue
              ? { codeValue: params.filters.codeValue }
              : {}),
          },
        }
      : {}),
    ...(params.filters?.styleTypeId
      ? { styleTypeId: params.filters.styleTypeId }
      : {}),
    ...(level === 'VARIANT' && params.filters?.styleIds?.length
      ? { styleId: params.filters.styleIds[0] }
      : {}),
    ...(level === 'SKU' && params.filters?.styleIds?.length
      ? { styleIds: params.filters.styleIds }
      : {}),
    ...(level === 'SKU' && params.filters?.variantIds?.length
      ? { variantIds: params.filters.variantIds }
      : {}),
  };
}

export function useCertificationStyles(
  params: Omit<CertificationStylesParams, 'page'>,
  enabled: boolean,
) {
  const level = params.level ?? 'STYLE';
  const fetchFn = LEVEL_FETCH[level];
  return useInfiniteQuery({
    queryKey: certificationKeys.styles(params),
    queryFn: ({ pageParam }) =>
      fetchFn(buildStylesBody(params, pageParam as number)),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.flatMap(p => p.data).length;
      return fetched < (lastPage.metadata?.pagination?.total ?? 0)
        ? allPages.length + 1
        : undefined;
    },
    enabled,
    staleTime: 30_000,
  });
}

/** Paged (non-infinite) variant of {@link useCertificationStyles} for the
 *  DataTable-based picker: fetches a single 1-based `page` and returns the
 *  raw {@link CertificationStylesPage} (data + pagination total). */
export function useCertificationStylesPage(
  params: Omit<CertificationStylesParams, 'page'>,
  page: number,
  enabled: boolean,
) {
  const fetchFn = LEVEL_FETCH[params.level ?? 'STYLE'];
  return useQuery({
    queryKey: [...certificationKeys.styles(params), 'page', page],
    queryFn: () => fetchFn(buildStylesBody(params, page)),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export type LinkedStylesScope = 'client' | 'admin';

export interface LinkedStylesPage {
  rows: CertificationLinkedStyle[];
  total: number;
}

/** Prefix for every page of a certificate's linked products on the management
 *  side, which reads the unscoped admin endpoint. */
export const adminCertificationLinkedStylesKey = (certId: string) =>
  ['admin', 'certifications', 'linked-styles', certId] as const;

/** The query string of one linked-products page. Only the filters that are
 *  set travel, so the key stays stable while the user types nothing. */
export function buildLinkedStylesQs(params: {
  page: number;
  limit: number;
  filters?: CertificationLinkedStylesFilters | undefined;
  sort?: CertificationLinkedStylesSort | undefined;
}): string {
  const qs = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  const f = params.filters ?? {};
  if (f.name?.trim()) qs.set('name', f.name.trim());
  if (f.codeId) qs.set('codeId', f.codeId);
  if (f.codeValue?.trim()) qs.set('codeValue', f.codeValue.trim());
  if (f.styleTypeId) qs.set('styleTypeId', f.styleTypeId);
  if (f.productLevel) qs.set('productLevel', f.productLevel);
  if (params.sort) {
    qs.set('sortBy', params.sort.field);
    qs.set('sortOrder', params.sort.order);
  }
  return qs.toString();
}

/**
 * One page of the products a certificate is linked to, every level mixed,
 * newest first (both endpoints sort by `created_at DESC`). This is the list the old dashboard's
 * overview shows; the picker tables cannot produce it because their filters
 * take parent ids, not an arbitrary id set.
 *
 * The two endpoints answer different envelopes (`meta.totalCount` on the
 * client, `metadata.pagination.total` on admin), normalised here so the table
 * reads one shape.
 */
export function useCertificationLinkedStylesPage(opts: {
  scope: LinkedStylesScope;
  certId: string;
  page: number;
  limit: number;
  enabled: boolean;
  filters?: CertificationLinkedStylesFilters;
  sort?: CertificationLinkedStylesSort;
}) {
  const { scope, certId, page, limit, enabled, filters, sort } = opts;
  const baseKey =
    scope === 'admin'
      ? adminCertificationLinkedStylesKey(certId)
      : certificationKeys.linkedStyles(certId);
  return useQuery({
    queryKey: [...baseKey, page, limit, filters ?? null, sort ?? null],
    queryFn: async (): Promise<LinkedStylesPage> => {
      const qs = buildLinkedStylesQs({ page, limit, filters, sort });
      if (scope === 'admin') {
        const res = await fetchAdminCertificationLinkedStyles(certId, qs);
        return {
          rows: res.data ?? [],
          total: res.metadata?.pagination?.total ?? 0,
        };
      }
      const res = await fetchCertificationLinkedStyles(certId, qs);
      return { rows: res.data ?? [], total: res.meta?.totalCount ?? 0 };
    },
    enabled: enabled && !!certId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useExistingCertificates(
  standardId: string,
  companyId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: certificationKeys.existing(standardId, companyId),
    queryFn: async () => {
      const res = await fetchExistingCertificates(standardId, companyId);
      return (res?.data ?? []).map(c => ({
        id: c.id,
        name: c.name,
        documentNumber: c.documentNumber ?? null,
      }));
    },
    enabled: enabled && !!standardId && !!companyId,
    staleTime: 30_000,
  });
}
